<template>
  <div>
    <div v-if="providersStore.loading" class="loading">Loading providers...</div>
    <template v-else>
      <div class="search-wrap">
        <input
          v-model.trim="searchQuery"
          type="search"
          class="search-input"
          placeholder="Search by name, location, county or panel..."
          aria-label="Search providers"
        />
      </div>
      <div v-if="displayedProviders.length === 0" class="empty-state">
        {{ searchQuery ? 'No providers match your search.' : 'No providers found.' }}
      </div>
      <div v-else class="table-container">
        <table>
          <thead>
            <tr>
              <th @click="sortBy('name')" class="sortable">
                <span class="th-inner">
                  Provider name
                  <span class="sort-icon">{{ sortIcon('name') }}</span>
                </span>
              </th>
              <th @click="sortBy('location')" class="sortable">
                <span class="th-inner">
                  Physical location
                  <span class="sort-icon">{{ sortIcon('location') }}</span>
                </span>
              </th>
              <th @click="sortBy('county')" class="sortable">
                <span class="th-inner">
                  County
                  <span class="sort-icon">{{ sortIcon('county') }}</span>
                </span>
              </th>
              <th @click="sortBy('panelAccess')" class="sortable">
                <span class="th-inner">
                  Panel access
                  <span class="sort-icon">{{ sortIcon('panelAccess') }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="provider in displayedProviders" :key="provider.id">
              <td>{{ provider.name || '-' }}</td>
              <td>{{ provider.location || '-' }}</td>
              <td>{{ provider.county || '-' }}</td>
              <td>{{ provider.panelAccess || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useProvidersStore } from '../../stores/providers';

const providersStore = useProvidersStore();

const sortField = ref('name');
const sortOrder = ref('asc');
const searchQuery = ref('');

/* eslint-disable no-unused-vars */
const displayedProviders = computed(() => {
  let list = [...(providersStore.providers || [])];
  const q = searchQuery.value.toLowerCase();
  if (q) {
    list = list.filter((p) => String(p.name ?? '').toLowerCase().includes(q)
      || String(p.location ?? '').toLowerCase().includes(q)
      || String(p.county ?? '').toLowerCase().includes(q)
      || String(p.panelAccess ?? '').toLowerCase().includes(q));
  }
  list.sort((a, b) => {
    const aVal = a[sortField.value] ?? '';
    const bVal = b[sortField.value] ?? '';
    const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
    return sortOrder.value === 'asc' ? cmp : -cmp;
  });
  return list;
});

function sortIcon(field) {
  if (sortField.value !== field) return '⇅';
  return sortOrder.value === 'asc' ? '↑' : '↓';
}

function sortBy(field) {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortOrder.value = 'asc';
  }
}
/* eslint-enable no-unused-vars */

onMounted(() => {
  providersStore.fetchProviders();
});
</script>

<style scoped>
.loading,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: var(--text-muted, #666);
}

.search-wrap {
  margin-bottom: 16px;
}

.search-input {
  width: 100%;
  max-width: 400px;
  padding: 12px 16px;
  font-size: 14px;
  color: #1c1b1f;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-input::placeholder {
  color: #79747e;
}

.search-input:hover {
  border-color: #2babe2;
}

.search-input:focus {
  border-color: #23a6df;
  box-shadow: 0 0 0 2px rgba(30, 138, 210, 0.2);
}

.table-container {
  overflow-x: auto;
  margin-top: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
}

thead {
  background: linear-gradient(
  135deg,
  #1e90c815 0%,
  #2babe215 25%,
  #5fc7ea15 50%,
  #7fe0ff15 75%,
  #a6ecff15 100%
);
}

th,
td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
}

th {
  font-weight: 600;
  color: #1c1b1f;
  letter-spacing: 0.1px;
  text-transform: uppercase;
  font-size: 12px;
}

.th-inner {
  display: flex;
  align-items: center;
  gap: 8px;
}

td {
  font-size: 14px;
  color: #49454f;
}

tbody tr {
  transition: background-color 0.2s;
}

tbody tr:hover {
  background-color: #f5f5f5;
}

th.sortable {
  cursor: pointer;
  user-select: none;
}

th.sortable:hover {
  background: linear-gradient(
  135deg,
  #167fa615 0%,
  #2babe215 30%,
  #4fc1eb15 55%,
  #7fdcff15 80%,
  #c6f4ff15 100%
);
}

.sort-icon {
  font-size: 14px;
  opacity: 0.8;
}
</style>
